import React from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import {
  Heart, HandHeart, Users, Building2, Wallet, Wrench,
  Megaphone, GraduationCap, MapPin, Mail, Phone, ArrowRight,
  Sparkles, ShieldCheck, FileText, Check, Euro, Handshake, Globe2,
  Zap, ClipboardCheck, Rocket, ExternalLink, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSection, resolveImage } from '../contexts/ContentContext';
import LiveDonationStats from '../components/LiveDonationStats';

const ICONS = {
  Heart, HandHeart, Users, Building2, Wallet, Wrench,
  Megaphone, GraduationCap, MapPin, Mail, Phone, Sparkles,
  ShieldCheck, FileText, Euro, Handshake, Globe2, Zap,
  ClipboardCheck, Rocket
};

export default function SupportUs() {
  const data = useSection('support');
  const settings = useSection('settings');

  const hero = data.hero || {};
  const financial = data.financial || {};
  const human = data.human || {};
  const national = data.national || {};
  const deploy = data.deploy || {};
  const success = data.success || {};

  const heroImage = resolveImage(hero.image);
  const localImage = resolveImage(human.image);

  return (
    <>
      {/* HERO */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 via-white to-sky-50 relative overflow-hidden">
        <div className="absolute top-24 -left-32 w-96 h-96 bg-emerald-200/40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-16 -right-24 w-96 h-96 bg-sky-200/40 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <Reveal>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-emerald-100 shadow-sm text-xs font-bold uppercase tracking-wider text-emerald-700 mb-5" data-testid="support-eyebrow">
                  <HandHeart className="w-3.5 h-3.5" />
                  {hero.eyebrow || '4 — Je donne, je soutiens'}
                </span>
                <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-sky-950 mb-6 leading-tight" data-testid="support-title">
                  {hero.title || 'Ensemble,'} <span className="gradient-text">{hero.titleHighlight || 'faisons grandir les Kiosques à Eau'}</span>
                </h1>
                <p className="text-lg text-slate-600 leading-relaxed mb-8" data-testid="support-description">
                  {hero.description}
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-2">
              <Reveal delay={150}>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-br from-emerald-200/50 to-sky-200/50 rounded-3xl blur-2xl"></div>
                  <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center">
                    {heroImage ? (
                      <img src={heroImage} alt="Communauté" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-8">
                        <Users className="w-24 h-24 mx-auto text-emerald-500 mb-3" />
                        <p className="text-emerald-800 font-semibold">Mosaïque de portraits</p>
                        <p className="text-xs text-emerald-600 mt-1">À téléverser depuis le CMS</p>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE DONATION STATS — Transparence (masqué à la demande de l'utilisateur) */}
      {/* <LiveDonationStats objective={25000} /> */}

      {/* NOS BESOINS - Introduction (bloc supprimé à la demande de l'utilisateur) */}

      {/* BESOINS FINANCIERS — section supprimée à la demande de l'utilisateur */}

      {/* BESOINS HUMAINS - LOCAL */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-emerald-500 mx-auto flex items-center justify-center mb-5 shadow-lg shadow-sky-500/30">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-display font-bold text-3xl lg:text-4xl text-sky-950 mb-3">
                Nos besoins <span className="gradient-text">humains</span>
              </h3>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Localement et à l'échelle nationale, nous avons besoin de bénévoles pour porter le projet.
              </p>
            </div>
          </Reveal>

          {/* LOCALEMENT */}
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            <Reveal>
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-4">
                  <MapPin className="w-3 h-3" /> Localement
                </span>
                <h4 className="font-display font-bold text-2xl text-sky-950 mb-4">
                  Chaque Kiosque s'appuie sur un Cercle de Citoyens
                </h4>
                <div className="space-y-4 text-slate-700 leading-relaxed">
                  <p>
                    {human.local1 || "Chaque nouveau Kiosque à Eau s'appuie sur un Cercle de Citoyens. Ces bénévoles jouent un rôle essentiel en organisant notamment des dégustations d'eau, directement devant le kiosque ou lors de manifestations locales où l'association tient un stand."}
                  </p>
                  <p>
                    {human.local2 || "L'eau distribuée est de grande qualité, économique et respectueuse de l'environnement. Pourtant, adopter une nouvelle habitude de consommation demande du temps. Notre plus grand défi est d'accompagner durablement les habitants afin qu'ils prennent l'habitude de venir remplir leurs contenants au kiosque."}
                  </p>
                </div>
                {human.localPdfUrl && (
                  <a
                    href={human.localPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    data-testid="support-local-pdf-btn"
                    className="group inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-sm font-semibold shadow-md hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    {human.localPdfLabel || 'En savoir plus'}
                  </a>
                )}
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-br from-sky-200/40 to-emerald-200/40 rounded-3xl blur-2xl"></div>
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center">
                  {localImage ? (
                    <img src={localImage} alt="Bénévoles en action" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center p-8">
                      <Users className="w-20 h-20 mx-auto text-sky-500 mb-3" />
                      <p className="text-sky-800 font-semibold">Photo bénévoles / dégustation</p>
                      <p className="text-xs text-sky-600 mt-1">À téléverser depuis le CMS</p>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          </div>

          {/* À L'ÉCHELLE NATIONALE */}
          <Reveal>
            <div className="p-8 lg:p-10 rounded-3xl bg-gradient-to-br from-slate-900 via-sky-950 to-emerald-900 text-white relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-emerald-400/20 rounded-full blur-3xl"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                    <Globe2 className="w-6 h-6 text-emerald-300" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-emerald-200 font-bold">À l'échelle nationale</span>
                    <h4 className="font-display font-bold text-2xl text-white">Missions réalisables à distance</h4>
                  </div>
                </div>
                <p className="text-slate-200 mb-6 max-w-2xl">
                  Certaines missions peuvent être réalisées à distance. Nous nous appuyons sur les plateformes
                  de bénévolat et de mécénat de compétences pour trouver les expertises dont nous avons besoin.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(national.missions || [
                    { icon: "Megaphone", label: "Communication" },
                    { icon: "Euro", label: "Recherche de financements" },
                    { icon: "Sparkles", label: "Développement du site internet" },
                    { icon: "Rocket", label: "Google Ads for Nonprofits" },
                    { icon: "GraduationCap", label: "Mécénat de compétences" },
                    { icon: "ClipboardCheck", label: "Accompagnement administratif" },
                  ]).map((m, i) => {
                    const Icon = ICONS[m.icon] || Sparkles;
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="w-9 h-9 rounded-xl bg-emerald-400/20 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-emerald-300" />
                        </div>
                        <span className="text-sm text-slate-100 font-medium">{m.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Reveal>

          {/* JE VEUX AIDER — CTA plateforme bénévolat */}
          <Reveal delay={100}>
            <a
              href="https://www.jeveuxaider.gouv.fr/organisations/5966-kiosques-a-eau"
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-6 block p-6 rounded-3xl bg-gradient-to-r from-sky-50 to-emerald-50 border-2 border-sky-100 hover:border-sky-300 hover:shadow-lg transition-all"
              data-testid="jeveuxaider-cta"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 via-white to-red-500 flex items-center justify-center shrink-0 shadow-md">
                  <Handshake className="w-6 h-6 text-sky-900" />
                </div>
                <div className="flex-1">
                  <p className="text-slate-700 leading-relaxed">
                    Pour renforcer notre présence sur le terrain et pour les missions pouvant être réalisées
                    à distance, nous nous appuyons sur la plateforme <strong className="text-sky-900">« Je veux aider »</strong>.
                  </p>
                  <p className="mt-2 text-sm text-sky-700 font-semibold flex items-center gap-1.5 group-hover:gap-2 transition-all">
                    Rejoignez-nous sur JeVeuxAider.gouv.fr
                    <ExternalLink className="w-3.5 h-3.5" />
                  </p>
                </div>
              </div>
            </a>
          </Reveal>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 rounded-full bg-white border border-sky-100 shadow-sm text-xs font-bold uppercase tracking-wider text-sky-700 mb-4">
                Développement
              </span>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-4">
                Développer des Kiosques à Eau
                <br />
                <span className="gradient-text">partout en France</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                {deploy.intro || "Après l'ouverture du premier Kiosque à Eau à Lons, notre objectif est d'en déployer une dizaine sur l'agglomération paloise afin de mener une expérimentation à grande échelle."}
              </p>
            </div>
          </Reveal>

          {/* CTA question */}
          <Reveal>
            <div className="text-center mb-12 p-8 rounded-3xl bg-gradient-to-r from-sky-500 to-emerald-500 text-white shadow-xl shadow-sky-500/20">
              <p className="font-display font-bold text-2xl lg:text-3xl mb-2">
                {deploy.question || "Vous souhaitez installer un ou plusieurs Kiosques à Eau ?"}
              </p>
              <p className="text-white/90">Dans votre Communauté d'agglomérations ou de communes, devant votre entreprise.</p>
            </div>
          </Reveal>

          {/* Bloc « Nous vous accompagnons / Nous étudions chaque projet » supprimé à la demande de l'utilisateur */}

          {/* Financing info */}
          <Reveal>
            <div className="p-6 lg:p-8 rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 mb-8">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0">
                  <Euro className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-display font-bold text-xl text-sky-950 mb-3">Financement du projet</h4>
                  <p className="text-slate-700 mb-3 text-sm leading-relaxed">
                    L'association ne peut pas financer directement les installations. Chaque porteur de projet doit
                    donc réunir les financements nécessaires, soit environ <strong>25 000 € par kiosque</strong>.
                  </p>
                  <p className="text-slate-700 mb-3 text-sm leading-relaxed">
                    En revanche, notre statut d'<strong>association reconnue d'intérêt général</strong> permet aux donateurs et
                    aux entreprises de bénéficier des dispositifs de <strong>réduction fiscale</strong> liés aux dons et au
                    mécénat de compétences.
                  </p>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Nous proposons également des solutions de paiement adaptées aux associations, aux entreprises
                    et aux collectivités.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Contact block */}
          <Reveal>
            <div className="p-8 rounded-3xl bg-white shadow-lg border border-sky-100 text-center">
              <p className="font-display font-bold text-xl text-sky-950 mb-4">Une question ?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href={`mailto:${settings?.contact?.email || 'contact@kiosques-a-eau.fr'}`}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-sky-50 hover:bg-sky-100 text-sky-800 font-medium transition-colors"
                  data-testid="support-contact-email"
                >
                  <Mail className="w-4 h-4" />
                  {settings?.contact?.email || 'contact@kiosques-a-eau.fr'}
                </a>
                <a
                  href={`tel:${(settings?.contact?.phone || '07 65 24 81 35').replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-medium transition-colors"
                  data-testid="support-contact-phone"
                >
                  <Phone className="w-4 h-4" />
                  {settings?.contact?.phone || '07 65 24 81 35'}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

    </>
  );
}
